import dagre from 'dagre';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactFlow, {
	Background,
	Controls,
	MiniMap,
	addEdge,
	useEdgesState,
	useNodesState,
	useReactFlow,
	type Connection,
	type Edge,
	type ReactFlowInstance,
} from 'reactflow';
import 'reactflow/dist/style.css';

import { useClipboard } from '../contexts/ClipboardContext';
import { useIsDark } from '../contexts/ThemeContext';
import { MEMORY_OPTIMIZED_REACTFLOW_CONFIG, ReactFlowMemoryUtils } from '../lib/reactflow-config';
import { hasStartNode, isEmptyWorkflow, isTriggerNodeType } from '../lib/workflow-utils';
import type {
	AdvancedNodeData,
	CustomNode,
	FlowControlNodeData,
	IntegrationsNodeData,
	ProcessingNodeData,
	TriggersNodeData,
} from '../types/nodes';
import { ConditionEdge } from './ConditionEdge';
import {
	ApprovalNode,
	LoggerNode,
	LoopNode,
	ParallelNode,
	RetryNode,
	StorageNode,
	SwitchNode,
	ValidatorNode,
	WaitNode,
	WebhookCallerNode,
	WebhookListenerNode,
} from './nodes/AdvancedNodes';
import { EndNode } from './nodes/FlowControlNodes';
import {
	DatabaseNode,
	EmailNode,
	GoogleSheetsNode,
	HubSpotNode,
	SlackNode,
	SubmissionFormNode,
} from './nodes/IntegrationNodes';
import { ApiCallNode, ConditionNode, PdfNode, TransformNode } from './nodes/ProcessingNodes';
import { ResponseNode } from './nodes/ResponseNode';
import { StripeNode } from './nodes/StripeNode';
import { StartHttpEndpointNode, StartScheduleNode, StartWebhookNode } from './nodes/TriggerNodes';

const nodeTypes = {
	end: EndNode,
	response: ResponseNode,
	'api-call': ApiCallNode,
	transform: TransformNode,
	condition: ConditionNode,
	pdf: PdfNode,
	database: DatabaseNode,
	email: EmailNode,
	slack: SlackNode,
	hubspot: HubSpotNode,
	'google-sheets': GoogleSheetsNode,
	stripe: StripeNode,
	// Trigger nodes
	'start.form': SubmissionFormNode,
	'start.webhook': StartWebhookNode,
	'start.schedule': StartScheduleNode,
	'start.http-endpoint': StartHttpEndpointNode,
	// Advanced nodes
	loop: LoopNode,
	wait: WaitNode,
	'webhook-listener': WebhookListenerNode,
	approval: ApprovalNode,
	logger: LoggerNode,
	validator: ValidatorNode,
	storage: StorageNode,
	'webhook-caller': WebhookCallerNode,
	switch: SwitchNode,
	retry: RetryNode,
	parallel: ParallelNode,
} as const;

// Start with empty canvas - let users choose their own trigger nodes
const initialNodes: CustomNode[] = [];

const getLayoutedElements = (nodes: CustomNode[], edges: Edge[], direction = 'TB') => {
	const dagreGraph = new dagre.graphlib.Graph();
	dagreGraph.setDefaultEdgeLabel(() => ({}));

	// Configure graph with proper spacing and constraints
	dagreGraph.setGraph({
		rankdir: direction,
		nodesep: 60, // Vertical spacing between nodes
		edgesep: 30, // Horizontal spacing between edges
		ranksep: 80, // Spacing between ranks
		marginx: 50, // Horizontal margin
		marginy: 50, // Vertical margin
		// Add constraints to keep nodes within reasonable bounds
		align: 'UL', // Align to upper-left
	});

	// Set nodes with appropriate dimensions based on type
	nodes.forEach(node => {
		let width = 280;
		let height = 80;

		// Adjust dimensions for different node types
		if (
			node.type === 'start.form' ||
			node.type === 'start.webhook' ||
			node.type === 'start.schedule'
		) {
			width = 300;
			height = 100;
		} else if (node.type === 'end') {
			width = 200;
			height = 60;
		} else if (node.type === 'condition') {
			width = 320;
			height = 120;
		} else if (node.type === 'stripe') {
			width = 300;
			height = 100;
		}

		dagreGraph.setNode(node.id, { width, height });
	});

	// Set edges
	edges.forEach(edge => {
		dagreGraph.setEdge(edge.source, edge.target);
	});

	// Calculate layout
	dagre.layout(dagreGraph);

	// Get positioned nodes with proper bounds checking
	const layoutedNodes = nodes.map(node => {
		const nodeWithPosition = dagreGraph.node(node.id);
		let x = nodeWithPosition.x;
		let y = nodeWithPosition.y;

		// Ensure nodes stay within reasonable bounds to prevent off-screen positioning
		const minX = 50; // Minimum X position
		const minY = 50; // Minimum Y position
		const maxX = 2000; // Reasonable maximum X position
		const maxY = 1500; // Reasonable maximum Y position

		// Constrain positions to prevent off-screen nodes
		x = Math.max(minX, Math.min(maxX, x));
		y = Math.max(minY, Math.min(maxY, y));

		return {
			...node,
			position: { x, y },
		};
	});

	return layoutedNodes;
};

const createNodeData = (
	type: keyof typeof nodeTypes,
	label: string
):
	| FlowControlNodeData
	| ProcessingNodeData
	| IntegrationsNodeData
	| AdvancedNodeData
	| TriggersNodeData => {
	if (type === 'end' || type === 'response') {
		return {
			label,
			category: 'flow-control',
			type,
			config: type === 'response' ? { format: 'success' } : {},
		};
	}

	if (type === 'api-call' || type === 'transform' || type === 'condition' || type === 'pdf') {
		return {
			label,
			category: 'processing',
			type,
			config: {},
		};
	}

	if (
		type === 'loop' ||
		type === 'wait' ||
		type === 'webhook-listener' ||
		type === 'approval' ||
		type === 'logger' ||
		type === 'validator' ||
		type === 'storage' ||
		type === 'webhook-caller' ||
		type === 'switch' ||
		type === 'retry' ||
		type === 'parallel'
	) {
		return {
			label,
			category: 'advanced',
			type,
			config: {},
		};
	}

	// Handle trigger nodes
	if (type === 'start.form') {
		// Reclassified submission form as start.form trigger
		return {
			label,
			category: 'triggers' as const,
			type: 'start.form',
			config: {
				name: label, // Initialize name to match label for proper synchronization
				fields: [
					{
						name: 'email',
						label: 'Email Address',
						type: 'email',
						required: true,
						placeholder: 'Enter your email address',
					},
				],
			},
			isSubmissionForm: true,
			formFields: [
				{
					name: 'email',
					label: 'Email Address',
					type: 'email',
					required: true,
					placeholder: 'Enter your email address',
				},
			],
		} as any; // Cast to avoid type issues with extended properties
	}

	if (type === 'start.webhook' || type === 'start.schedule' || type === 'start.http-endpoint') {
		return {
			label,
			category: 'triggers' as const,
			type: type as 'start.webhook' | 'start.schedule' | 'start.http-endpoint',
			config: type === 'start.http-endpoint' ? { slug: '', timeoutMs: 30000 } : {},
		};
	}

	// Handle integration nodes
	if (
		type === 'database' ||
		type === 'email' ||
		type === 'slack' ||
		type === 'hubspot' ||
		type === 'google-sheets' ||
		type === 'stripe'
	) {
		return {
			label,
			category: 'integrations',
			type,
			config: {},
		};
	}

	// Fallback for any unhandled types
	return {
		label,
		category: 'integrations',
		type: 'database', // Default fallback
		config: {},
	};
};

export function useWorkflowState(onNodeChange?: () => void, onEdgeChange?: () => void) {
	const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
	const [edges, setEdges, onEdgesChange] = useEdgesState([]);
	const [undoStack, setUndoStack] = useState<{ nodes: CustomNode[]; edges: Edge[] }[]>([]);
	const [redoStack, setRedoStack] = useState<{ nodes: CustomNode[]; edges: Edge[] }[]>([]);

	// Maximum undo stack size to prevent memory leaks
	const MAX_UNDO_STACK_SIZE = 50;

	// Save current state to undo stack before making changes
	const saveState = useCallback(() => {
		setUndoStack(stack => {
			const newStack = [
				...stack,
				{
					nodes: nodes.map(n => ({ ...n })),
					edges: edges.map(e => ({ ...e })),
				},
			];
			// Limit stack size to prevent memory leaks
			return newStack.slice(-MAX_UNDO_STACK_SIZE);
		});
		setRedoStack([]);
	}, [nodes, edges]);

	// Wrap setNodes and setEdges to automatically save state and trigger onNodeChange
	const setNodesWithUndo = useCallback(
		(updater: any) => {
			saveState();
			if (typeof updater === 'function') {
				setNodes(nodes => {
					const newNodes = updater(nodes);
					// Trigger onNodeChange callback
					if (onNodeChange) {
						onNodeChange();
					}
					return newNodes;
				});
			} else {
				setNodes(updater);
				// Trigger onNodeChange callback
				if (onNodeChange) {
					onNodeChange();
				}
			}
		},
		[saveState, setNodes, onNodeChange]
	);

	const setEdgesWithUndo = useCallback(
		(updater: any) => {
			saveState();
			if (typeof updater === 'function') {
				setEdges(edges => {
					const newEdges = updater(edges);
					// Trigger onNodeChange callback
					if (onNodeChange) {
						onNodeChange();
					}
					// Trigger onEdgeChange callback
					if (onEdgeChange) {
						onEdgeChange();
					}
					return newEdges;
				});
			} else {
				setEdges(updater);
				// Trigger onNodeChange callback
				if (onNodeChange) {
					onNodeChange();
				}
				// Trigger onEdgeChange callback
				if (onEdgeChange) {
					onEdgeChange();
				}
			}
		},
		[saveState, setEdges, onNodeChange, onEdgeChange]
	);

	// Handle node changes with undo
	const onNodesChangeWithUndo = useCallback(
		(changes: any) => {
			// Only save state for non-position changes
			if (changes.some((change: any) => change.type !== 'position')) {
				saveState();
				// Trigger onNodeChange callback for non-position changes
				if (onNodeChange) {
					onNodeChange();
				}
			}
			onNodesChange(changes);
		},
		[onNodesChange, saveState, onNodeChange]
	);

	// Handle edge changes with undo
	const onEdgesChangeWithUndo = useCallback(
		(changes: any) => {
			saveState();
			// Trigger onNodeChange callback
			if (onNodeChange) {
				onNodeChange();
			}
			// Trigger onEdgeChange callback
			if (onEdgeChange) {
				onEdgeChange();
			}
			onEdgesChange(changes);
		},
		[onEdgesChange, saveState, onNodeChange, onEdgeChange]
	);

	// Undo/Redo handlers
	const undo = useCallback(() => {
		if (undoStack.length === 0) return;

		const previousState = undoStack[undoStack.length - 1];
		const newUndoStack = undoStack.slice(0, -1);

		setRedoStack(stack => [
			...stack,
			{
				nodes: nodes.map(n => ({ ...n })),
				edges: edges.map(e => ({ ...e })),
			},
		]);
		setUndoStack(newUndoStack);
		setNodes(previousState.nodes);
		setEdges(previousState.edges);
	}, [undoStack, nodes, edges, setNodes, setEdges]);

	const redo = useCallback(() => {
		if (redoStack.length === 0) return;

		const nextState = redoStack[redoStack.length - 1];
		const newRedoStack = redoStack.slice(0, -1);

		setUndoStack(stack => [
			...stack,
			{
				nodes: nodes.map(n => ({ ...n })),
				edges: edges.map(e => ({ ...e })),
			},
		]);
		setRedoStack(newRedoStack);
		setNodes(nextState.nodes);
		setEdges(nextState.edges);
	}, [redoStack, nodes, edges, setNodes, setEdges]);

	return {
		nodes,
		edges,
		setNodes: setNodesWithUndo,
		setEdges: setEdgesWithUndo,
		onNodesChange: onNodesChangeWithUndo,
		onEdgesChange: onEdgesChangeWithUndo,
		undo,
		redo,
		canUndo: undoStack.length > 0,
		canRedo: redoStack.length > 0,
		saveState,
	};
}

export function WorkflowEditor({
	onReactFlowInit,
	onNodeChange,
	onEdgeChange,
}: {
	onReactFlowInit?: (instance: ReactFlowInstance) => void;
	onNodeChange?: () => void;
	onEdgeChange?: () => void;
}) {
	const isDark = useIsDark();
	const {
		nodes,
		edges,
		setNodes,
		setEdges,
		onNodesChange,
		onEdgesChange,
		saveState,
		undo,
		redo,
	} = useWorkflowState(onNodeChange, onEdgeChange);
	const { pasteNode, hasNodeInClipboard } = useClipboard();
	const { getNodes, getEdges } = useReactFlow();
	const reactFlowWrapper = useRef<HTMLDivElement>(null);
	const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
	const [connectionError, setConnectionError] = useState<string | null>(null);

	// Clear connection error after 3 seconds
	useEffect(() => {
		if (connectionError) {
			const timeout = setTimeout(() => {
				setConnectionError(null);
			}, 3000);
			return () => clearTimeout(timeout);
		}
	}, [connectionError]);

	// Add keyboard event handlers for undo/redo
	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if ((event.metaKey || event.ctrlKey) && event.key === 'z') {
				if (event.shiftKey) {
					redo();
				} else {
					undo();
				}
				event.preventDefault();
			}

			// Handle paste (Ctrl+V or Cmd+V)
			if ((event.metaKey || event.ctrlKey) && event.key === 'v') {
				if (hasNodeInClipboard) {
					handlePasteNode();
					event.preventDefault();
				}
			}
		};

		document.addEventListener('keydown', onKeyDown);
		return () => {
			document.removeEventListener('keydown', onKeyDown);
		};
	}, [undo, redo, hasNodeInClipboard]);

	// Handle pasting a node
	const handlePasteNode = useCallback(() => {
		const pastedNode = pasteNode();
		if (pastedNode && reactFlowInstance && reactFlowWrapper.current) {
			const nodeType = pastedNode.type;
			const isEmpty = isEmptyWorkflow(nodes);
			const hasStart = hasStartNode(nodes);
			const isTriggerNode = nodeType ? isTriggerNodeType(nodeType) : false;

			// Apply the same validation rules as onDrop
			if (isTriggerNode) {
				// Rule 2: Only allow one trigger node per canvas
				if (hasStart) {
					setConnectionError('Each workflow can only have one trigger node.');
					return;
				}
			} else {
				// Rule 1: Only allow trigger nodes on empty canvas
				// Exception: Allow end nodes to be pasted anywhere
				if (nodeType && nodeType !== 'end' && (isEmpty || !hasStart)) {
					setConnectionError(
						'Start with a trigger node first. Only trigger nodes can be added to an empty workflow.'
					);
					return;
				}
			}

			// Calculate position for pasted node (offset from current view center)
			const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
			const centerX = reactFlowBounds.width / 2;
			const centerY = reactFlowBounds.height / 2;

			const position = reactFlowInstance.project({
				x: centerX,
				y: centerY,
			});

			// Update the node position
			pastedNode.position = position;

			// Add the node to the workflow
			setNodes((nds: CustomNode[]) => nds.concat(pastedNode));

			// Trigger onEdgeChange callback for autosave
			if (onEdgeChange) {
				onEdgeChange();
			}
		}
	}, [pasteNode, reactFlowInstance, setNodes, nodes, setConnectionError, onEdgeChange]);

	// Memoized debounce function to prevent memory leaks
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const debounce = useCallback((func: Function, wait: number) => {
		return (...args: any[]) => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
			debounceRef.current = setTimeout(() => func(...args), wait);
		};
	}, []);

	// Debounced layout function
	const debouncedLayout = useCallback(
		debounce((edges: Edge[]) => {
			// Get current nodes state to avoid using stale data
			const currentNodes = getNodes();
			if (currentNodes && currentNodes.length > 0) {
				const layoutedNodes = getLayoutedElements(currentNodes, edges);
				// Only update positions, preserve all other node data including labels
				setNodes(
					layoutedNodes.map(layoutedNode => {
						const originalNode = currentNodes.find(n => n.id === layoutedNode.id);
						if (originalNode) {
							return {
								...originalNode, // Preserve all original data including labels
								position: layoutedNode.position, // Only update position
							};
						}
						return layoutedNode;
					})
				);

				// Trigger onEdgeChange callback for autosave since layout changes might affect edges
				if (onEdgeChange) {
					onEdgeChange();
				}

				// Give the nodes time to update their positions before fitting the view
				// Only fit view if there are significant changes to avoid jarring movements
				setTimeout(() => {
					reactFlowInstance?.fitView({ padding: 0.1 }); // Reduced padding for less jarring movement
				}, 100);
			}
		}, 300), // Increased debounce time to reduce excessive layout calls
		[reactFlowInstance, setNodes, getNodes, onEdgeChange]
	);

	// Listen for node updates from BaseNode components
	useEffect(() => {
		const handleNodeUpdate = (event: CustomEvent) => {
			// Save undo state if requested
			if (event.detail?.shouldSaveState) {
				saveState();
			}

			// Trigger auto layout on node configuration changes
			// This ensures the layout updates when nodes are configured
			const currentEdges = getEdges();
			if (currentEdges.length > 0) {
				debouncedLayout(currentEdges);
			}

			// Trigger onNodeChange callback when a node is updated
			if (onNodeChange) {
				onNodeChange();
			}

			// Trigger onEdgeChange callback for autosave
			if (onEdgeChange) {
				onEdgeChange();
			}
		};

		window.addEventListener('workflow-node-updated', handleNodeUpdate as EventListener);
		return () => {
			window.removeEventListener('workflow-node-updated', handleNodeUpdate as EventListener);
		};
	}, [onNodeChange, saveState, debouncedLayout, getEdges, onEdgeChange]);

	// Cleanup debounce timeout and React Flow instance on unmount
	useEffect(() => {
		return () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
			// Clean up React Flow instance to prevent memory leaks
			if (reactFlowInstance) {
				ReactFlowMemoryUtils.cleanup(reactFlowInstance);
			}
		};
	}, [reactFlowInstance]);

	// Modified onConnect with validation rules and condition node handling
	const onConnect = useCallback(
		(params: Connection | Edge) => {
			// Get current nodes state to avoid using stale data
			const currentNodes = getNodes();

			// Find the source and target nodes
			const sourceNode = currentNodes.find(node => node.id === params.source);
			const targetNode = currentNodes.find(node => node.id === params.target);

			if (!sourceNode || !targetNode) {
				return; // Invalid connection
			}

			// ✅ Allow submission forms to connect directly to processing nodes
			// ✅ Allow all other valid node connections
			// No special validation needed - start nodes are deprecated

			setEdges((eds: Edge[]) => {
				// Create the new edge with enhanced metadata for condition nodes
				let newEdge = addEdge(params, eds);

				// If this is a connection from a condition node, add proper metadata
				if (sourceNode.type === 'condition') {
					const outgoingEdges = eds.filter((e: any) => e.source === params.source);
					const isFirstConnection = outgoingEdges.length === 0;

					// Find the edge we just added
					const addedEdge = newEdge.find(
						(e: any) => e.source === params.source && e.target === params.target
					);

					if (addedEdge) {
						// Set edge type to use custom condition edge component
						addedEdge.type = 'condition';

						// Set condition and label based on connection order
						if (isFirstConnection) {
							// First connection is typically the "true" path
							addedEdge.data = {
								...addedEdge.data,
								condition: 'true',
								label: 'True',
							};
						} else {
							// Second connection is typically the "false" path
							addedEdge.data = {
								...addedEdge.data,
								condition: 'false',
								label: 'False',
							};
						}
					}
				}

				// Trigger layout after connection
				debouncedLayout(newEdge);

				// Trigger onEdgeChange callback for autosave
				if (onEdgeChange) {
					onEdgeChange();
				}

				return newEdge;
			});
		},
		[getNodes, debouncedLayout, setEdges, onEdgeChange]
	);

	const onDragOver = useCallback((event: React.DragEvent) => {
		event.preventDefault();
		event.dataTransfer.dropEffect = 'move';
	}, []);

	// Modified onDrop to save state before making changes
	const onDrop = useCallback(
		(event: React.DragEvent) => {
			event.preventDefault();

			const type = event.dataTransfer.getData(
				'application/reactflow'
			) as keyof typeof nodeTypes;
			const label = event.dataTransfer.getData('application/reactflow-label');

			// check if we have valid data
			if (!type || !reactFlowWrapper.current || !reactFlowInstance) {
				return;
			}

			const isEmpty = isEmptyWorkflow(nodes);
			const hasStart = hasStartNode(nodes);
			const isTriggerNode = isTriggerNodeType(type);

			// Editor Behavior Rules validation
			if (isTriggerNode) {
				// Rule 2: Only allow one trigger node per canvas
				if (hasStart) {
					setConnectionError('Each workflow can only have one trigger node.');
					return;
				}
			} else {
				// Rule 1: Only allow trigger nodes on empty canvas
				if (isEmpty || !hasStart) {
					setConnectionError(
						'Start with a trigger node first. Only trigger nodes can be added to an empty workflow.'
					);
					return;
				}
			}

			const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
			const position = reactFlowInstance.project({
				x: event.clientX - reactFlowBounds.left,
				y: event.clientY - reactFlowBounds.top,
			});

			const newNode: CustomNode = {
				id: `${type}-${nodes.length + 1}`,
				type,
				position,
				data: createNodeData(type, label),
			};

			setNodes((nds: CustomNode[]) => nds.concat(newNode));

			// Trigger onEdgeChange callback for autosave (since adding nodes might affect edge layout)
			if (onEdgeChange) {
				onEdgeChange();
			}
		},
		[nodes, reactFlowInstance, setNodes, setConnectionError, onEdgeChange]
	);

	// Initialize React Flow and pass instance to parent
	const onInit = useCallback(
		(instance: ReactFlowInstance) => {
			setReactFlowInstance(instance);
			if (onReactFlowInit) {
				onReactFlowInit(instance);
			}

			// Check performance thresholds
			const warnings = ReactFlowMemoryUtils.checkPerformanceThresholds(nodes, edges);
			if (warnings.length > 0) {
				console.warn('⚠️ Performance warnings:', warnings);
			}
		},
		[onReactFlowInit, nodes, edges]
	);

	return (
		<div ref={reactFlowWrapper} className="relative w-full h-full">
			{/* Dark Mode Controls CSS Overrides */}
			<style>
				{`
					.react-flow__controls {
						border: 1px solid ${isDark ? '#374151' : '#e5e7eb'};
						border-radius: 6px;
						box-shadow: ${isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)'};
					}

					.react-flow__controls-button {
						background: ${isDark ? '#1f2937' : '#ffffff'} !important;
						border-bottom: 1px solid ${isDark ? '#374151' : '#e5e7eb'} !important;
						color: ${isDark ? '#e5e7eb' : '#374151'} !important;
						transition: all 0.2s ease-in-out;
					}

					.react-flow__controls-button:hover {
						background: ${isDark ? '#374151' : '#f3f4f6'} !important;
						color: ${isDark ? '#f9fafb' : '#111827'} !important;
					}

					.react-flow__controls-button:first-child {
						border-top-left-radius: 6px;
						border-top-right-radius: 6px;
					}

					.react-flow__controls-button:last-child {
						border-bottom-left-radius: 6px;
						border-bottom-right-radius: 6px;
						border-bottom: none !important;
					}

					.react-flow__controls-button svg {
						fill: currentColor !important;
					}
				`}
			</style>

			{/* Connection Error Toast */}
			{connectionError && (
				<div className="absolute top-4 left-1/2 z-50 px-6 py-3 max-w-md text-red-700 bg-red-100 rounded-lg border border-red-400 shadow-lg transform -translate-x-1/2 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200">
					<div className="flex items-center">
						<svg className="mr-2 w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
							<path
								fillRule="evenodd"
								d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
								clipRule="evenodd"
							/>
						</svg>
						<span className="text-sm font-medium">{connectionError}</span>
					</div>
				</div>
			)}

			<ReactFlow
				nodes={nodes}
				edges={edges}
				onNodesChange={onNodesChange}
				onEdgesChange={onEdgesChange}
				onConnect={onConnect}
				onInit={onInit}
				onDragOver={onDragOver}
				onDrop={onDrop}
				nodeTypes={nodeTypes}
				edgeTypes={{
					condition: ConditionEdge,
				}}
				{...MEMORY_OPTIMIZED_REACTFLOW_CONFIG}
			>
				<Background color={isDark ? '#374151' : '#e5e7eb'} gap={20} size={1} />
				<Controls
					showInteractive={false}
					style={{
						backgroundColor: isDark ? '#1f2937' : '#ffffff',
						borderColor: isDark ? '#374151' : '#e5e7eb',
						borderRadius: 6,
						boxShadow: isDark
							? '0 4px 6px -1px rgba(0, 0, 0, 0.3)'
							: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
					}}
				/>
				<MiniMap
					style={{ backgroundColor: isDark ? '#111827' : '#f9fafb' }}
					maskColor={isDark ? 'rgba(17,24,39,0.85)' : 'rgba(255,255,255,0.8)'}
					nodeColor={() => (isDark ? '#1f2937' : '#e5e7eb')}
					nodeStrokeColor={() => (isDark ? '#6b7280' : '#9ca3af')}
					nodeBorderRadius={4}
				/>
			</ReactFlow>
		</div>
	);
}
